import { FC, FormEvent, ChangeEvent, useState, useEffect } from 'react';

import { clsx } from 'clsx';
import { User, Save, ChevronDown, ChevronUp } from 'lucide-react';

import { NovaPoshtaCitySelect } from '../../components/nova_poshta/NovaPoshtaCitySelect';
import {
    Avatar,
    AvatarFallback,
    AvatarImage,
} from '../../components/ui/Avatar';
import { Button } from '../../components/ui/Button';
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    CardDescription,
} from '../../components/ui/Card';
import { Input } from '../../components/ui/Input';
import { Label } from '../../components/ui/Label';
import { EditUserFormValues } from '../../types';

interface Props {
    formData: EditUserFormValues;
    errors: Partial<Record<keyof EditUserFormValues, string>>;
    onInputChange: (
        field: keyof EditUserFormValues,
        value: string | File | null,
    ) => void;
    onSubmit: (e: FormEvent) => void;
    processing?: boolean;
    currentAvatarUrl?: string;
    cityRequired?: boolean;
    isOpen: boolean;
    onToggle: () => void;
}

export const PersonalDataForm: FC<Props> = ({
    formData,
    errors,
    onInputChange,
    onSubmit,
    processing = false,
    currentAvatarUrl,
    cityRequired = false,
    isOpen,
    onToggle,
}) => {
    const [previewUrl, setPreviewUrl] = useState<string | null>(
        currentAvatarUrl || null,
    );

    useEffect(() => {
        if (currentAvatarUrl) {
            setPreviewUrl(currentAvatarUrl);
        }
    }, [currentAvatarUrl]);

    const handleAvatarChange = (e: ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            onInputChange('avatar', file);
            const objectUrl = URL.createObjectURL(file);
            setPreviewUrl(objectUrl);

            // Clean up memory when the component unmounts or preview changes
            return () => URL.revokeObjectURL(objectUrl);
        }
    };
    return (
        <Card id="personal-data" className="h-fit scroll-mt-4">
            <CardHeader
                className="cursor-pointer transition-colors hover:bg-muted/50"
                onClick={onToggle}
            >
                <CardTitle className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <User className="h-5 w-5 text-primary" />
                        Особисті дані
                    </div>
                    {isOpen ? (
                        <ChevronUp className="h-4 w-4 text-muted-foreground" />
                    ) : (
                        <ChevronDown className="h-4 w-4 text-muted-foreground" />
                    )}
                </CardTitle>
                <CardDescription>
                    Основна інформація про ваш профіль
                </CardDescription>
            </CardHeader>
            {isOpen && (
                <CardContent>
                    <form onSubmit={onSubmit} className="space-y-4">
                        <div className="flex flex-col items-center gap-4 sm:flex-row">
                            <div
                                className={clsx('rounded-full', {
                                    'ring-2 ring-red-600 ring-offset-2':
                                        errors.avatar,
                                })}
                            >
                                <Avatar className="h-24 w-24">
                                    <AvatarImage
                                        src={previewUrl || undefined}
                                        alt={formData.nickname}
                                    />
                                    <AvatarFallback>
                                        {formData.nickname
                                            .slice(0, 2)
                                            .toUpperCase()}
                                    </AvatarFallback>
                                </Avatar>
                            </div>
                            <div className="flex flex-col items-center gap-4 sm:items-start">
                                <Label
                                    htmlFor="avatar"
                                    className="cursor-pointer"
                                >
                                    <span className="rounded-md bg-secondary px-4 py-2 text-sm font-medium text-secondary-foreground hover:bg-secondary/80">
                                        Змінити фото
                                    </span>
                                    <Input
                                        id="avatar"
                                        type="file"
                                        accept="image/png, image/jpeg"
                                        className="hidden"
                                        onChange={handleAvatarChange}
                                    />
                                </Label>
                                <p className="text-center text-xs text-muted-foreground sm:text-left">
                                    JPG, PNG. Максимум 5MB.
                                </p>
                            </div>
                        </div>
                        {errors.avatar && (
                            <p className="text-center text-sm text-red-600 sm:text-left">
                                {errors.avatar}
                            </p>
                        )}
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                            <div className="space-y-2">
                                <Label htmlFor="first_name">Ім&#39;я</Label>
                                <Input
                                    id="first_name"
                                    value={formData.first_name}
                                    onChange={(e) =>
                                        onInputChange(
                                            'first_name',
                                            e.target.value,
                                        )
                                    }
                                    placeholder="Введіть ваше ім'я"
                                    className={clsx({
                                        'border-red-600': errors.first_name,
                                    })}
                                />
                                {errors.first_name && (
                                    <p className="text-sm text-red-600">
                                        {errors.first_name}
                                    </p>
                                )}
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="last_name">Прізвище</Label>
                                <Input
                                    id="last_name"
                                    value={formData.last_name}
                                    onChange={(e) =>
                                        onInputChange(
                                            'last_name',
                                            e.target.value,
                                        )
                                    }
                                    placeholder="Введіть ваше прізвище"
                                    className={clsx({
                                        'border-red-600': errors.last_name,
                                    })}
                                />
                                {errors.last_name && (
                                    <p className="text-sm text-red-600">
                                        {errors.last_name}
                                    </p>
                                )}
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="nickname">Нікнейм *</Label>
                            <Input
                                id="nickname"
                                value={formData.nickname}
                                onChange={(e) =>
                                    onInputChange('nickname', e.target.value)
                                }
                                placeholder="Введіть ваш нікнейм"
                                className={clsx({
                                    'border-red-600': errors.nickname,
                                })}
                                required
                            />
                            {errors.nickname && (
                                <p className="text-sm text-red-600">
                                    {errors.nickname}
                                </p>
                            )}
                        </div>

                        <div className="space-y-1">
                            <NovaPoshtaCitySelect
                                id="profile_city"
                                value={formData.city ?? ''}
                                required={cityRequired}
                                initialFallback={Boolean(
                                    formData.city &&
                                        !formData.nova_poshta_city_ref,
                                )}
                                onSelect={(city) => {
                                    onInputChange('city', city.name);
                                    onInputChange(
                                        'nova_poshta_city_ref',
                                        city.ref,
                                    );
                                }}
                                onFallbackInput={(city) => {
                                    onInputChange('city', city);
                                    onInputChange('nova_poshta_city_ref', '');
                                }}
                                onSelectionClear={(city) => {
                                    onInputChange('city', city);
                                    onInputChange('nova_poshta_city_ref', '');
                                }}
                                error={errors.city}
                            />
                            <p className="text-xs text-muted-foreground">
                                {cityRequired
                                    ? 'Обов’язкове, оскільки у вас увімкнений самовивіз.'
                                    : 'Необов’язкове. Знадобиться, якщо ви ввімкнете самовивіз.'}
                            </p>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="email">Email *</Label>
                            <Input
                                id="email"
                                type="email"
                                value={formData.email}
                                onChange={(e) =>
                                    onInputChange('email', e.target.value)
                                }
                                placeholder="Введіть ваш email"
                                className={clsx({
                                    'border-red-600': errors.email,
                                })}
                                required
                            />
                            {errors.email && (
                                <p className="text-sm text-red-600">
                                    {errors.email}
                                </p>
                            )}
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="telegram_username">
                                Telegram *
                            </Label>
                            <Input
                                id="telegram_username"
                                value={formData.telegram_username}
                                onChange={(e) =>
                                    onInputChange(
                                        'telegram_username',
                                        e.target.value,
                                    )
                                }
                                placeholder="@username"
                                className={clsx({
                                    'border-red-600': errors.telegram_username,
                                })}
                                required
                            />
                            {errors.telegram_username && (
                                <p className="text-sm text-red-600">
                                    {errors.telegram_username}
                                </p>
                            )}
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="bio">Біографія</Label>
                            <textarea
                                id="bio"
                                value={formData.bio}
                                onChange={(e) =>
                                    onInputChange('bio', e.target.value)
                                }
                                placeholder="Розкажіть про себе..."
                                className={clsx(
                                    'flex min-h-[80px] w-full rounded-xl border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50',
                                    {
                                        'border-red-600': errors.bio,
                                    },
                                )}
                            />
                            {errors.bio && (
                                <p className="text-sm text-red-600">
                                    {errors.bio}
                                </p>
                            )}
                        </div>

                        <Button
                            type="submit"
                            className="w-full"
                            disabled={processing}
                        >
                            <Save className="mr-2 h-4 w-4" />
                            {processing ? 'Збереження...' : 'Зберегти зміни'}
                        </Button>
                    </form>
                </CardContent>
            )}
        </Card>
    );
};
